import * as React from 'react';
import { Component } from 'react';

import compose from 'recompose/compose';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';

import { StyleSheet, css } from '@coursera/coursera-ui';
import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import Submit from 'bundles/authentication/components/Submit';
import { MODES } from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/authentication';

export type componentProps = {
  className?: string;
  navigate: (path: MODES, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
};

type Props = componentProps & { router: InjectedRouter };

export type State = {
  magicLinkSent: boolean;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });
const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '24px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '0px 30px',
  },
  description: {
    textAlign: 'center',
    wordWrap: 'break-word',
    whiteSpace: 'pre-line',
    lineHeight: '24px',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
  },
  buttonContainer: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
    margin: '20px auto',
  },
  navigation: {
    margin: '12px auto 0',
    display: 'flex !important',
    textAlign: 'center',
    fontSize: '14px',
    color: '#0056D2 !important',
    textDecoration: 'underline',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
  },
  navigationIcon: {
    marginRight: '0.6rem',
  },
  section: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
    fontSize: '24px',
    lineHeight: '30px',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '34px',
      lineHeight: '42px',
    },
  },
  submit: {
    marginTop: '12px',
    marginBottom: '20px',
    width: '265px',
  },
  spamText: {
    marginBottom: '0px',
    color: '#636363',
    fontSize: '14px',
    marginTop: '12px',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
    textAlign: 'center',
  },
  boldEmail: {
    fontWeight: '700',
  },
});

class ExpiredLink extends Component<Props, State> {
  state = {
    magicLinkSent: false,
  };

  navigateToLogin = () => {
    const { navigate } = this.props;

    navigate(MODES.LOGIN);
  };

  sendNewLink = () => {
    const { router, magicLinkRedirect } = this.props;
    const email = router.location.query.email;
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });

    this.setState({ magicLinkSent: true });
  };

  render() {
    const { className } = this.props;
    const { magicLinkSent } = this.state;

    return (
      <TrackedDiv trackingName="expired_login_link_modal" trackClicks={false} withVisibilityTracking>
        <section {...css(className, styles.container)} aria-live="polite">
          <header {...css(styles.section)}>
            <h1 {...css(styles.title)}>{_t('Your link expired')}</h1>
          </header>

          <TrackedSubmit
            {...css(styles.submit, styles.section, styles.buttonContainer)}
            trackingName="send_new_link_button"
            disabled={magicLinkSent}
            onClick={this.sendNewLink}
          >
            {_t('Email me a new link')}
          </TrackedSubmit>
          <div {...css(styles.section)}>
            <p {...css(styles.spamText)}>{_t("Can't find your link? Check your spam folder.")}</p>

            <LinkButton {...css(styles.navigation)} onClick={this.navigateToLogin}>
              <div {...css(styles.navigationIcon)}>
                <SvgArrowLeft color="#2A73CC" size={15} />
              </div>
              {_t('All login options')}
            </LinkButton>
          </div>
        </section>
      </TrackedDiv>
    );
  }
}

export default compose<Props, componentProps>(
  connectToRouter<Props, componentProps>((router) => ({ router })),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      page: 'expired_login_link_page',
    },
  }))
)(ExpiredLink);
